import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import React from 'react';
import { JobStatusRes, JobStatusSubRow } from './data/jobStatus';
import { dateFormat, timeFormat } from '../../../../contstant';
import { Badge } from '../../../../components/Common/UI/Badge';
import { StatusBadge } from '../../../../components/Common/StatusBadge';
import { StatusesType } from '../../../types';
import { MenuPopoverContent, Popover, PopoverTrigger } from '../../../../components/Common/UI/Popover';
import Icons from '../../../../components/Common/Icons/Icons';
import Link from '../../../../components/Common/UI/Link';
import { Button } from '../../../../components/Common/UI/Button';
import { notifySuccess } from '../../../../components/Common/UI/Notify';

export function DateTimeDisplay({ date }: { date: string }) {
  return (
    <div className="child:leading-tight">
      <p>{dayjs(date).format(dateFormat)}</p>
      <p className="text-sm font-normal text-muted-foreground">{dayjs(date).format(timeFormat)}</p>
    </div>
  );
}

export function MutedSmallText({ children }: React.PropsWithChildren) {
  return <span className="text-muted-foreground text-sm">{children}</span>;
}

export const jobColumns: ColumnDef<JobStatusRes>[] = [
  {
    accessorKey: 'fileReceived',
    header: 'File Received',
    cell: ({ row }) => {
      const val: string = row.getValue('fileReceived');

      return <DateTimeDisplay date={val} />;
    },
  },
  {
    accessorKey: 'jobNum',
    header: 'Job ID',
  },
  {
    accessorKey: 'applicationId',
    header: 'Application ID',
  },
  {
    accessorKey: 'filesCount',
    header: '# Of files',
    cell: ({ row }) => <Badge variant="counter">{row.getValue('filesCount')}</Badge>,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusBadge status={row.getValue('status')} />,
    size: 128,
  },
];

export const jobSubRowsColumns: ColumnDef<JobStatusSubRow>[] = [
  {
    accessorKey: 'type',
    header: ' ',
    cell: ({ row }) => {
      const { type } = row.original;

      return (
        <Badge variant="default" className="text-primary bg-primary-10 text-base font-medium">
          {type.shortName}
        </Badge>
      );
    },
    size: 100,
  },
  {
    accessorKey: 'scheduledDropDate',
    header: 'Scheduled drop date',
    // cell: ({ row }) => (dayjs(row.getValue('scheduledDropDate')).format(dateFormat)),
    cell: ({ row }) => {
      const val: string = row.getValue('scheduledDropDate');

      return val ? <DateTimeDisplay date={val} /> : <MutedSmallText>waiting to assign date</MutedSmallText>;
    },
  },
  {
    accessorKey: 'actualDropDate',
    header: 'Actual drop date',
    cell: ({ row }) => {
      const val: string = row.getValue('actualDropDate');

      return val ? <DateTimeDisplay date={val} /> : <MutedSmallText>waiting to assign date</MutedSmallText>;
    },
  },
  {
    accessorKey: 'procId',
    header: 'Proc ID',
  },
  {
    accessorKey: 'numOfAccounts',
    header: '# Of Accounts',
    cell: ({ row }) => <Badge variant="counter">{row.getValue('numOfAccounts')}</Badge>,
  },
  {
    accessorKey: 'numOfPages',
    header: '# Of Pages',
    cell: ({ row }) => <Badge variant="counter">{row.getValue('numOfPages')}</Badge>,
  },
  {
    accessorKey: 'numOfImages',
    header: '# Of Images',
    cell: ({ row }) => <Badge variant="counter">{row.getValue('numOfImages')}</Badge>,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusBadge status={row.getValue('status')} variant="transparent" />,
    size: 128,
  },
  {
    id: 'actions',
    accessorKey: '',
    header: '',
    cell: ({ row }) => {
      const { type, jobId } = row.original;
      const status: StatusesType = row.getValue('status') || 'draft';

      return (
        <Popover>
          <PopoverTrigger className="block">
            <Icons.More />
          </PopoverTrigger>
          <MenuPopoverContent>{renderActionsByStatus(status, type.fullName, jobId)}</MenuPopoverContent>
        </Popover>
      );
    },
    size: 50,
  },
];

export const renderActionsByStatus = (status: StatusesType, id: string | number, jobId?: string) => {
  const jobQuery = jobId ? `&job=${jobId}` : '';

  if (status === 'running') {
    return (
      <>
        <Link to={`${id}`}>
          <Icons.DownloadFilled className="text-primary" />
          Download CSV
        </Link>
        <Link to={`${id}?tab=job-details${jobQuery}`}>
          <Icons.JobDetails />
          Job Details
        </Link>
        <Link to={`${id}?tab=delivery-info${jobQuery}`}>
          <Icons.DeliveryInfo className="text-primary" />
          Delivery Information
        </Link>
        <Link to={`${id}?tab=geo-delivery${jobQuery}`}>
          <Icons.Earth />
          Geographic Delivery Data
        </Link>
        {/*
        <Link to={`${id}`}>
          <Icons.Approve className="text-primary" />
          Approve
        </Link>
        <Link to={`${id}`}>
          <Icons.Unapprove className="text-primary" />
          Disapprove
        </Link>
        <Link to={`${id}`}>
          <Icons.Delete className="text-primary" />
          Delete
        </Link> */}
        <Button
          size="auto"
          variant="ghost"
          title="Delete"
          className="text-primary hover:text-primary"
          onClick={() => notifySuccess('Approved')}
        >
          <Icons.Approve />
          Approve
        </Button>
        <Button
          size="auto"
          variant="ghost"
          title="Delete"
          className="text-primary hover:text-primary"
          onClick={() => notifySuccess('Disapproved')}
        >
          <Icons.Unapprove />
          Disapprove
        </Button>
        <Button
          size="auto"
          variant="ghost"
          title="Delete"
          className="text-primary hover:text-primary"
          onClick={() => notifySuccess('Deleted!')}
        >
          <Icons.Delete />
          Delete
        </Button>
      </>
    );
  }

  return (
    <>
      <Link to={`${id}?tab=job-details${jobQuery}`}>
        <Icons.DownloadFilled className="text-primary" />
        Download CSV
      </Link>
      <Link to={`${id}?tab=job-details${jobQuery}`}>
        <Icons.JobDetails />
        Job Details
      </Link>
    </>
  );
};
